import { useEffect, useState } from "react"

type Theme = "light" | "dark"

export interface UseThemeOptions {
  /** localStorage key. Default: "lbb-theme". */
  storageKey?: string
  /**
   * Toggling target. Default: `document.documentElement`. Apps embedded inside
   * a host page (or that want to scope dark mode to a subtree) can pass any
   * Element here.
   */
  target?: () => Element | null
  /** Class name added when `theme === "dark"`. Default: "dark". */
  darkClass?: string
}

function readInitial(storageKey: string): Theme {
  if (typeof window === "undefined") return "light"
  const persisted = window.localStorage.getItem(storageKey) as Theme | null
  if (persisted === "light" || persisted === "dark") return persisted
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
}

/**
 * Persisted light/dark toggle. Apps should also render a pre-paint <script> in
 * their <head> that reads the same storage key and applies the dark class, to
 * avoid the white flash on first load. See glarchive/web/index.html for a sample.
 */
export function useTheme(opts: UseThemeOptions = {}): {
  theme: Theme
  toggle: () => void
  setTheme: (t: Theme) => void
} {
  const storageKey = opts.storageKey ?? "lbb-theme"
  const darkClass = opts.darkClass ?? "dark"
  const [theme, setTheme] = useState<Theme>(() => readInitial(storageKey))

  useEffect(() => {
    const target = opts.target ? opts.target() : document.documentElement
    if (!target) return
    if (theme === "dark") target.classList.add(darkClass)
    else target.classList.remove(darkClass)
    try { window.localStorage.setItem(storageKey, theme) } catch {}
  }, [theme, storageKey, darkClass, opts.target])

  return { theme, setTheme, toggle: () => setTheme((t) => (t === "dark" ? "light" : "dark")) }
}
